import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as types from '../../../constants/actionTypes';
import Moment from 'moment';
import { DATE_FORMAT, GRAPH_STATUSES, MOBILE_WINDOW_BREAKPOINT } from '../../../constants/constants';
import { Routes } from '../../../constants/routes';
import { NavigateToRouteWithDashboardParams } from '../../../helpers/routing';
import GraphArc from '../../Common/GraphArc/GraphArc';
import Card from '../../Common/Card';
import BarCard from '../../Common/BarCard/BarCard';
import Graph100StackedBar from '../../Common/Graph100StackedBar';
import Notification from '../../Common/Notification';
import Icon from '../../Common/Icon';
import Button from '../../Common/Button';
import TaskDetailManager from '../../TaskList/TaskDetail/TaskDetailManager';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import './milestones.scss';

export class Milestones extends Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			isLoadingGraph: true,
			isLoadingListing: true,
			isLoadingPrerequisites: true,
			showMore: true,
			showMorePrerequisites: true,
			isTaskDetailOpen: false,
			editTaskId: null,
			milestonesGraph: {},
			milestoneTasks: [],
			milestoneTasksWithPrerequisites: [],
			locationUsers: [],
			searchParams: {
				pageSize: 8,
				pageNumber: 1,
				locationKey: this.props.locationKey,
				currentDate: Moment.utc().format(DATE_FORMAT),
				statusName: '',
			},
			prerequisitesSearchParams: {
				pageSize: 8,
				pageNumber: 1,
				locationKey: this.props.locationKey,
			},
		};
	}

	componentDidMount = async () => {
		this.init();
	};

	componentDidUpdate(prevProps) {
		// reload when the user changes locations
		// they could be searching for a location so make sure there is a value
		if (this.props.locationKey && this.props.locationKey !== prevProps.locationKey) {
			this.init();
		}
	}

	init = () => {
		const defaultParams = {
			pageNumber: 1,
			locationKey: this.props.locationKey,
			currentDate: Moment.utc().format(DATE_FORMAT),
			statusName: '',
		};
		const defaultPrerequisitesParams = {
			pageSize: 8,
			pageNumber: 1,
			locationKey: this.props.locationKey,
		};
		this.setState(
			{
				milestoneTasks: [],
				milestoneTasksWithPrerequisites: [],
				milestonesGraph: {},
				isLoadingGraph: true,
				isLoadingListing: true,
				isLoadingPrerequisites: true,
				showMorePrerequisites: true,
				searchParams: { ...this.state.searchParams, ...defaultParams },
				prerequisitesSearchParams: { ...this.state.prerequisitesSearchParams, ...defaultPrerequisitesParams },
			},
			async () => {
				await Promise.all([
					this.getResponsibleUsersForLocations(),
					this.loadMilestones(),
					this.loadListing(),
					this.loadMilestoneTasksWithPrerequisites(),
				]);
			}
		);
	};

	getResponsibleUsersForLocations = async () => {
		try {
			const response = await this.props.locationTaskActions.searchResponsibleUsers([this.props.locationKey]);
			if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_SUCCESS) {
				this.setState({ locationUsers: response.payload.users });
			} else {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	loadMilestones = async () => {
		if (!this.props.locationKey) {
			return;
		}
		try {
			const response = await this.props.locationDashboardActions.loadMilestones(this.props.locationKey);
			if (response.type === types.LOAD_MILESTONES_BY_LOCATION_SUCCESS) {
				this.setState({
					milestonesGraph: response.payload.locationMilestonesGraph,
				});
			} else if (response.type === types.LOAD_MILESTONES_BY_LOCATION_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoadingGraph: false });
		}
	};

	loadListing = async () => {
		try {
			const response = await this.props.locationDashboardActions.loadMilestonesListing(this.state.searchParams);
			if (response.type === types.LOAD_MILESTONES_LISTING_SUCCESS) {
				// if at any point there are fewer than [searchParams.pageSize] results returned then we know we can't show more
				const showMore = response.payload.milestoneTasks.length < this.state.searchParams.pageSize ? false : true;
				const combinedTasks = this.state.milestoneTasks.concat(response.payload.milestoneTasks);
				this.setState({
					milestoneTasks: combinedTasks,
					showMore: showMore,
				});
			} else if (response.type === types.LOAD_MILESTONES_LISTING_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoadingListing: false });
		}
	};

	loadMilestoneTasksWithPrerequisites = async () => {
		if (!this.props.locationKey) {
			return;
		}
		try {
			const response = await this.props.locationDashboardActions.loadMilestoneTasksWithPrerequisites(this.state.prerequisitesSearchParams);
			if (response.type === types.LOAD_MILESTONE_TASKS_WITH_PREREQUISITES_SUCCESS) {
				// Check if there are fewer results than pageSize to determine if we can show more
				const showMorePrerequisites = response.payload.results.length < this.state.prerequisitesSearchParams.pageSize ? false : true;
				const combinedTasks = this.state.milestoneTasksWithPrerequisites.concat(response.payload.results);

				this.setState({
					milestoneTasksWithPrerequisites: combinedTasks,
					showMorePrerequisites: showMorePrerequisites,
				});
			} else if (response.type === types.LOAD_MILESTONE_TASKS_WITH_PREREQUISITES_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoadingPrerequisites: false });
		}
	};

	onClick_LocationMilestones = (urlParams) => {
		NavigateToRouteWithDashboardParams(Routes.Tasks, urlParams, false);
	};

	getTaskStatus = (task) => {
		if (task.coreStatusName !== GRAPH_STATUSES.Complete.label
			&& task.coreStatusName !== GRAPH_STATUSES.NotApplicable.label
			&& Moment.utc(task.targetDate) < Moment.utc().startOf('day')) {
			return 'status-past-due';
		}

		// Check for specific status matches first
		if (task.coreStatusName === GRAPH_STATUSES.Complete.label) {
			return 'status-complete';
		} else if (task.coreStatusName === GRAPH_STATUSES.PastDue.label) {
			return 'status-past-due';
		} else if (task.coreStatusName === GRAPH_STATUSES.InProgress.label) {
			return 'status-in-progress';
		} else if (task.coreStatusName === GRAPH_STATUSES.NotDue.label) {
			return 'status-not-due';
		} else if (task.coreStatusName === GRAPH_STATUSES.NotApplicable.label) {
			return 'status-not-applicable';
		}

		// Default to not due if no other status applies
		return 'status-not-due';
	};

	onCardClick = (task) => {
		this.setState({ isTaskDetailOpen: true, editTaskId: task.id });
	};

	onTaskDetailComplete = () => {
		this.setState({ isTaskDetailOpen: false });
		this.init();
	};

	onTaskDetailCancel = () => {
		this.setState({ isTaskDetailOpen: false });
	};

	onShowMore = () => {
		const newParams = {
			pageNumber: this.state.searchParams.pageNumber + 1,
		};
		this.setState(
			{
				isLoadingListing: true,
				searchParams: {
					...this.state.searchParams,
					...newParams,
				},
			},
			this.loadListing
		);
	};

	onShowMorePrerequisites = () => {
		const newParams = {
			pageNumber: this.state.prerequisitesSearchParams.pageNumber + 1,
		};
		this.setState(
			{
				isLoadingPrerequisites: true,
				prerequisitesSearchParams: {
					...this.state.prerequisitesSearchParams,
					...newParams,
				},
			},
			this.loadMilestoneTasksWithPrerequisites
		);
	};

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	/**
	 * Convert prerequisite status counts to graph data format
	 * Expected format: [{ id: 'StatusName', value: count, urlParams: {...} }]
	 */
	formatPrerequisiteGraphData = (totalPrerequisites) => {
		if (!totalPrerequisites || !Array.isArray(totalPrerequisites)) {
			return [];
		}

		// Initialize all possible statuses with 0 values
		const graphData = [
			{ id: GRAPH_STATUSES.NotApplicable.label, value: 0, urlParams: {} },
			{ id: GRAPH_STATUSES.Complete.label, value: 0, urlParams: {} },
			{ id: GRAPH_STATUSES.PastDue.label, value: 0, urlParams: {} },
			{ id: GRAPH_STATUSES.InProgress.label, value: 0, urlParams: {} },
			{ id: GRAPH_STATUSES.NotDue.label, value: 0, urlParams: {} },
		];

		// Map the prerequisite data to graph format
		totalPrerequisites.forEach((prereq) => {
			const dataPoint = graphData.find((item) => item.id === prereq.graphStatus);
			if (dataPoint) {
				dataPoint.value = prereq.count;
				// Add urlParams if needed for navigation
				dataPoint.urlParams = {
					statusName: prereq.graphStatus,
				};
			}
		});

		return graphData;
	};

	/**
	 * Get total count of all prerequisites for a task
	 */
	getTotalPrerequisitesCount = (totalPrerequisites) => {
		if (!totalPrerequisites || !Array.isArray(totalPrerequisites)) {
			return 0;
		}
		return totalPrerequisites.reduce((sum, prereq) => sum + (prereq.count || 0), 0);
	};

	/**
	 * Handle click on prerequisite graph bar
	 */
	onPrerequisiteGraphClick = (dataPoint) => {
		// You can implement navigation logic here similar to Projects widget
		// For now, we'll just log the click
		console.log('Prerequisite graph clicked:', dataPoint);
	};

	renderPrerequisiteBar = (task) => {
		const totalPrerequisites = task.totalPrerequisites || [];
		const completedPrerequisites = task.completedPrerequisites || 0;

		// Handle new array format (list of PrerequisiteCoreStatusCount)
		if (Array.isArray(totalPrerequisites)) {
			const totalCount = this.getTotalPrerequisitesCount(totalPrerequisites);

			if (totalCount === 0) {
				return null;
			}

			const graphData = this.formatPrerequisiteGraphData(totalPrerequisites);

			return (
				<div className='prerequisite-bar'>
					<div className='prerequisite-text'>Pre-Requisite Completion</div>
					<div className='prerequisite-graph'>
						<Graph100StackedBar data={graphData} onClick={this.onPrerequisiteGraphClick} />
					</div>
				</div>
			);
		}

		// Fallback for legacy format (simple integer - kept for backwards compatibility)
		const totalCount = typeof totalPrerequisites === 'number' ? totalPrerequisites : 0;

		if (totalCount === 0) {
			return null;
		}

		const completedWidth = totalCount > 0 ? (completedPrerequisites / totalCount) * 100 : 0;
		const remainingWidth = 100 - completedWidth;

		return (
			<div className='prerequisite-bar'>
				<div className='prerequisite-bar-container'>
					<div className='prerequisite-bar-completed' style={{ width: `${completedWidth}%` }}></div>
					<div className='prerequisite-bar-remaining' style={{ width: `${remainingWidth}%` }}></div>
				</div>
				<div className='prerequisite-text'>Pre-Requisite Completion</div>
				<div className='prerequisite-counts'>
					<span className='completed-count'>{completedPrerequisites}</span>
					<span className='separator'>|</span>
					<span className='total-count'>{totalCount}</span>
					<span className='remaining-count'>{totalCount - completedPrerequisites}</span>
				</div>
			</div>
		);
	};

	render() {
		return (
			<div className='milestones-widget'>
				<div className='row'>
					<div className='col-12'>
						<div className='dashboard-widget-title'>Milestones</div>
					</div>
				</div>

				<div className='row mt-2'>
					{!this.state.isLoadingGraph && (
						<div className='col-md-3 col-sm-12 quarter-card'>
							<Card>
								<GraphArc
									title='Location Milestones'
									data={this.state.milestonesGraph.data}
									totalUrlParams={this.state.milestonesGraph.totalUrlParams}
									onClick={this.onClick_LocationMilestones}
								/>
							</Card>
						</div>
					)}
					<div className='col-md-1 col-sm-12'>&nbsp;</div>
					{!this.state.isLoadingPrerequisites && this.state.milestoneTasksWithPrerequisites.length > 0 && (
						<div className='col-md-8 col-sm-12'>
							<LoadingIndicator isLoading={this.state.isLoadingPrerequisites}>
								{this.state.milestoneTasksWithPrerequisites.map((task) => (
									<BarCard key={`prereq-${task.id}`} handleClass={this.getTaskStatus(task)}>
										<div className='milestone-card-with-prereq' onClick={() => this.onCardClick(task)}>
											<div className='col-2 align-self-center'>
												<div className='dateCell'>{Moment.utc(task.targetDate).format(DATE_FORMAT)}</div>
											</div>
											<div className='col-6'>
												{task.name ? <div className='name'>{task.name}</div> : <div className='name'></div>}
												<div className='title'>
													<div>
														<Icon name='bolt me-2' stylePrefix='far' />
														{task.statusName}
													</div>
													{task.responsibleUser && (
														<div>
															<Icon name='user me-2 ms-4' stylePrefix='far' />
															{task.responsibleUser}
														</div>
													)}
													{task.projectName && (
														<div>
															<Icon name='calendar me-2 ms-4' stylePrefix='far' />
															{task.projectName}
														</div>
													)}
												</div>
											</div>
											<div className='col-3 align-self-center'>{this.renderPrerequisiteBar(task)}</div>
											<div className='col-1 align-self-center'>
												<Icon name='chevron-right' />
											</div>
										</div>
									</BarCard>
								))}
								{this.state.showMorePrerequisites && (
									<div className='showMore'>
										<Button
											type='button'
											text='Show More'
											bootstrapClass='link'
											name='showMorePrerequisites'
											onClick={this.onShowMorePrerequisites}
										/>
									</div>
								)}
							</LoadingIndicator>
						</div>
					)}
				</div>

				{this.state.isTaskDetailOpen && (
					<TaskDetailManager
						taskId={this.state.editTaskId}
						locationKey={this.props.locationKey}
						userId={this.props.user.id}
						responsibleUsers={this.state.locationUsers}
						customerRole={this.props.user.customerRole}
						onComplete={this.onTaskDetailComplete}
						onCancel={this.onTaskDetailCancel}
						filesActions={this.props.filesActions}
						locationTaskActions={this.props.locationTaskActions}
						isMobile={this.isMobile()}
					/>
				)}
			</div>
		);
	}
}

Milestones.propTypes = {
	locationKey: PropTypes.string, // not required - user could be searching for a location
	location: PropTypes.object,
	user: PropTypes.object.isRequired,
	locationDashboardActions: PropTypes.object.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	windowWidth: PropTypes.number,
};

function mapStateToProps(state) {
	return {
		user: state.user,
		windowWidth: state.window.width,
	};
}

export default connect(mapStateToProps, null)(Milestones);
